// Unterseite einer Statistik-Zeile: die einzelnen Kennzeichen des Bereichs,
// getrennt nach "Gesehen" und "Nicht gesehen" (docs/reference/
// statistik-detail-de-gesehen.png und die drei Geschwisterbilder,
// 08.09.2026).
//
// Der Bereich steht in der Adresse (/statistik/bundesland-Brandenburg),
// damit die Seite teilbar und nach einem Neuladen noch dieselbe ist. Welche
// Zeichen dazugehoeren, beantwortet lib/statistik.ts - dieselbe Quelle wie
// fuer die Zahl in der Zeile davor. Welche Sammlung gezeigt wird, steht als
// "?sammlung=" daneben (Statistik.tsx).
//
// Der Bereich "fehlend" ist der einzige, den es in der Original-App nicht
// gibt: er zeigt, was in der eigenen Sammlung liegt und in der
// angesehenen gemeinsamen fehlt, und traegt den Knopf zum Uebertragen.

import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star } from "lucide-react";
import { Kennzeichenabzeichen } from "../bausteine/Kennzeichenabzeichen.js";
import { Unterseite } from "../bausteine/Unterseite.js";
import { useBundle, type Kennzeichen, type Land } from "../lib/bundle.js";
import { aktiveSammlung } from "../lib/collections.js";
import {
  persoenlicheSammlung,
  useEintraegeUebernehmen,
  useEintragDetails,
  useEintragSetzenMitSpiegel,
  useEntries,
  type Eintrag,
} from "../lib/entries.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import {
  bereichAusSchluessel,
  bereichTitel,
  kennzeichenImBereich,
} from "../lib/statistik.js";

const REITER = ["Gesehen", "Nicht gesehen"] as const;
type Reiter = (typeof REITER)[number];

function untertitel(k: Kennzeichen): string {
  if (k.regionNames.length > 0) return k.regionNames.join(" / ");
  return k.derivation ?? k.code;
}

/**
 * Der kursive Zusatz hinter dem Ort. Im Original steht dort
 * "Landkreis Gransee • Historisch" (docs/reference/
 * statistik-detail-bb-nichtgesehen.png); "auslaufend" kommt dort nicht vor,
 * bekommt aber dieselbe Behandlung mit der Wortwahl des Hauptscreens.
 */
function zusatz(k: Kennzeichen): string | null {
  if (k.status === "historisch") return "Historisch";
  if (k.status === "auslaufend") return "Nicht mehr ausgegeben";
  return null;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function Zeile({
  kennzeichen,
  land,
  rechts,
}: {
  kennzeichen: Kennzeichen;
  land: Land | undefined;
  rechts: React.ReactNode;
}) {
  const hinweis = zusatz(kennzeichen);
  return (
    <div className="mb-2 flex items-center gap-4 rounded bg-flaeche-inhalt px-3 py-3 shadow-sm">
      <Kennzeichenabzeichen
        land={land}
        historisch={kennzeichen.status !== "aktuell"}
      />
      <div className="min-w-0 flex-1">
        <div className="text-lg font-bold">{kennzeichen.code}</div>
        <div className={`text-sm text-neutral-500 ${hinweis ? "italic" : ""}`}>
          {untertitel(kennzeichen)}
          {hinweis && ` • ${hinweis}`}
        </div>
      </div>
      {rechts}
    </div>
  );
}

export function StatistikDetail({
  nutzer,
  sammlungen,
}: {
  nutzer: Nutzer;
  sammlungen: Sammlung[];
}) {
  const { bereich: schluessel } = useParams();
  const [suche] = useSearchParams();
  const [reiter, setReiter] = useState<Reiter>("Gesehen");
  const bundle = useBundle();

  const settings = (nutzer.settings ?? {}) as {
    activeCollectionId?: number;
    spiegleInEigene?: boolean;
  };
  // Welche Sammlung angesehen wird, steht in der Adresse (Statistik.tsx).
  // Ohne Angabe gilt die aktive.
  const gewaehlte = sammlungen.find(
    (s) => s.id === Number(suche.get("sammlung")),
  );
  const sammlung =
    gewaehlte ?? aktiveSammlung(sammlungen, settings.activeCollectionId);
  const persoenliche = persoenlicheSammlung(sammlungen);
  const eintraege = useEntries(sammlung?.id);
  const eintragDetails = useEintragDetails(sammlung?.id);
  // Dieselbe Regel wie auf dem Hauptscreen: wer in einer geteilten Sammlung
  // sammelt und spiegeln laesst, traegt hier genauso in beide ein.
  const { setzen: eintragSetzen } = useEintragSetzenMitSpiegel(
    sammlung,
    sammlungen,
    settings.spiegleInEigene,
  );
  // Ist die eigene Sammlung zugleich die angesehene, teilen sich beide
  // Aufrufe denselben Query-Key und es geht keine zweite Anfrage raus.
  const eigene = useEntries(persoenliche?.id);
  const eigeneDetails = useEintragDetails(persoenliche?.id);
  const uebernehmen = useEintraegeUebernehmen(sammlung?.id);

  const laender = useMemo(() => bundle.data?.laender ?? [], [bundle.data]);
  const kennzeichen = useMemo(
    () => bundle.data?.kennzeichen ?? [],
    [bundle.data],
  );
  const bereich =
    schluessel === undefined ? undefined : bereichAusSchluessel(schluessel);

  const liste = useMemo(
    () =>
      bereich === undefined
        ? []
        : kennzeichenImBereich(bereich, kennzeichen, laender),
    [bereich, kennzeichen, laender],
  );

  const zurueck = sammlung
    ? `/statistik?sammlung=${sammlung.id}`
    : "/statistik";

  if (bereich === undefined) {
    return (
      <Unterseite
        titel="Statistik"
        zurueckZu={zurueck}
        kinder={
          <p className="text-sm text-neutral-500">
            Diesen Bereich gibt es nicht.
          </p>
        }
      />
    );
  }

  const landZu = new Map(laender.map((l) => [l.id, l]));
  const karte = eintraege.data;
  const eigeneKarte = eigene.data;

  function alsGesehenMarkieren(k: Kennzeichen) {
    if (!sammlung) return;
    eintragSetzen({ plateCodeId: k.id, punkte: k.points });
  }

  if (bereich.art === "fehlend") {
    const fehlend = liste.filter(
      (k) => eigeneKarte.has(k.id) && !karte.has(k.id),
    );
    const uebertragbar =
      persoenliche !== undefined &&
      sammlung !== undefined &&
      persoenliche.id !== sammlung.id;
    return (
      <Unterseite
        titel={bereichTitel(bereich, laender)}
        zurueckZu={zurueck}
        inhaltKlasse="mx-auto max-w-md p-1"
        kinder={
          <>
            <div className="mb-2 rounded bg-flaeche-inhalt px-4 py-3 shadow-sm">
              <p className="text-sm text-neutral-500">
                {fehlend.length === 1
                  ? "1 Kennzeichen aus deiner eigenen Sammlung fehlt in "
                  : `${fehlend.length} Kennzeichen aus deiner eigenen Sammlung fehlen in `}
                {sammlung?.name}.
              </p>
              {/* Datum und Punkte kommen aus dem eigenen Fund, nicht von
                  heute: siehe api/src/lib/uebertragen.ts. */}
              <button
                type="button"
                disabled={
                  !uebertragbar || fehlend.length === 0 || uebernehmen.isPending
                }
                onClick={() =>
                  persoenliche &&
                  uebernehmen.mutate({
                    vonCollectionId: persoenliche.id,
                    plateCodeIds: fehlend.map((k) => k.id),
                  })
                }
                className="mt-3 min-h-11 w-full rounded bg-akzent px-4 text-base font-medium text-white disabled:bg-neutral-300"
              >
                {uebernehmen.isPending
                  ? "Wird übertragen..."
                  : `Alle ${fehlend.length} übertragen`}
              </button>
              {uebernehmen.isError && (
                <p className="mt-2 text-sm text-red-700">
                  Das hat nicht geklappt. Bitte später noch einmal versuchen.
                </p>
              )}
              {uebernehmen.isSuccess && !uebernehmen.isPending && (
                <p className="mt-2 text-sm text-neutral-500">
                  {uebernehmen.data.uebertragen} übertragen.
                </p>
              )}
            </div>
            {fehlend.map((k) => {
              const eigenerEintrag: Eintrag | undefined = eigeneDetails.get(
                k.id,
              );
              return (
                <Zeile
                  key={k.id}
                  kennzeichen={k}
                  land={landZu.get(k.countryId)}
                  rechts={
                    eigenerEintrag && (
                      <span className="shrink-0 text-sm text-neutral-600">
                        {datum(eigenerEintrag.firstSeenAt)}
                      </span>
                    )
                  }
                />
              );
            })}
            {fehlend.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-neutral-500">
                Hier fehlt nichts mehr.
              </p>
            )}
          </>
        }
      />
    );
  }

  const gezeigt = liste.filter((k) =>
    reiter === "Gesehen" ? karte.has(k.id) : !karte.has(k.id),
  );

  return (
    <Unterseite
      titel={bereichTitel(bereich, laender)}
      zurueckZu={zurueck}
      inhaltKlasse="mx-auto max-w-md p-1"
      unterKopf={
        <div className="flex">
          {REITER.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReiter(r)}
              className={`flex-1 border-b-[3px] py-3 text-base ${
                reiter === r
                  ? "border-white text-white"
                  : "border-transparent text-white/70"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      }
      kinder={
        <>
          {gezeigt.map((k) => {
            const eintrag = eintragDetails.get(k.id);
            return (
              <Zeile
                key={k.id}
                kennzeichen={k}
                land={landZu.get(k.countryId)}
                rechts={
                  eintrag ? (
                    <span className="shrink-0 text-sm text-neutral-600">
                      {datum(eintrag.firstSeenAt)}
                    </span>
                  ) : (
                    // Im Original steht auf dieser Seite ein leerer Stern je
                    // Zeile. Er tut dasselbe wie der grosse auf dem
                    // Hauptscreen, nur ohne Umweg ueber das Eingabefeld.
                    <button
                      type="button"
                      onClick={() => alsGesehenMarkieren(k)}
                      aria-label={`${k.code} als gesehen markieren`}
                      className="flex min-h-11 min-w-11 shrink-0 items-center justify-center text-neutral-400"
                    >
                      <Star size={26} />
                    </button>
                  )
                }
              />
            );
          })}
          {gezeigt.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-neutral-500">
              {reiter === "Gesehen"
                ? "Hier ist noch nichts gesammelt."
                : "Alles gesehen."}
            </p>
          )}
        </>
      }
    />
  );
}
